import type { Node, HTMLElement as HTMLParserElement } from 'node-html-parser';
import type { ArticleBlock } from '../entities/article';
import type { Session } from '@benzinga/session';
//import { TAG_KEYS_MAP } from '../assets/markup/tag-map';
import { probeImage, validURL } from '@benzinga/utils';
import { decode } from 'html-entities';
import { ContentManager } from '@benzinga/content-manager';
import { BasicNewsManager } from '@benzinga/basic-news-manager';
import { simplyQueryInternalNews } from '@benzinga/internal-news-manager';
import { getReadNextData } from './related';

export const splitHTMLContentRaw = async (htmlString: string): Promise<Node[]> => {
  const { parse } = await import('node-html-parser');
  const body = parse(htmlString);
  const splitContent: Node[] = [];

  if (Array.isArray(body?.childNodes)) {
    body.childNodes.forEach(item => {
      splitContent.push(item);
    });
  }

  return splitContent;
};

export const splitHTMLContent = async (htmlString: string): Promise<string[]> => {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { parse } = await import('node-html-parser');
  const body = parse(htmlString);
  const splitContent: string[] = [];

  if (Array.isArray(body?.childNodes)) {
    body.childNodes.forEach(item => {
      splitContent.push(item.toString());
    });
  }

  return splitContent;
};

const notAllowedInPTags = ['ul', 'ol', 'div', 'p', 'form', 'table', 'img'];

const formatImageCoreBlock = async (element: HTMLParserElement): Promise<ArticleBlock> => {
  try {
    let attributes: Record<string, string> = {};

    if (element.attributes) {
      attributes = {
        alt: '',
        ...element?.attributes,
      };
    }

    const isImageHasExtension = (url: string): boolean => {
      if (!url) return false;
      return url.match(/\.(jpeg|jpg|gif|png|svg|webp|heic|tif|bmp)$/) != null;
    };

    const isImageSafe = (url: string): boolean => {
      return !url.includes('http://');
    };

    let skipImageSizeCheck = false;

    if (
      !isImageHasExtension(element?.attributes.src) ||
      !isImageSafe(element?.attributes.src) ||
      element?.attributes.src.startsWith('/')
    ) {
      skipImageSizeCheck = true;
    }

    if (!skipImageSizeCheck && (!attributes.width || !attributes.height)) {
      const sizes = await probeImage(element?.attributes.src);

      if (sizes?.width && !attributes.width) {
        attributes.width = sizes?.width as unknown as string;
      }

      if (sizes?.height) {
        if (attributes.width) {
          attributes.height = `${(Number(attributes.width) * sizes?.height) / sizes?.width}`;
        } else {
          attributes.height = sizes?.height as unknown as string;
        }
      }
    }

    return {
      blockName: 'core/image',
      hasChild: false,
      innerHTML: '',
      nodeType: 1,
      tag: 'img',
      tagAttributes: attributes,
    };
  } catch (e) {
    return {
      blockName: 'core/image',
      hasChild: false,
      innerHTML: '',
      nodeType: 1,
      tag: 'img',
      tagAttributes: element?.attributes || {},
    };
  }
};

const base = new URL('https://www.benzinga.com');
const isExternal = (url: string): boolean => new URL(url, base).hostname !== base.hostname;

export const formatElementBlock = async (
  session: Session,
  element: HTMLParserElement,
  sponsored?: boolean,
  articleId?: number,
): Promise<ArticleBlock> => {
  if (element?.rawTagName === 'img') {
    return await formatImageCoreBlock(element);
  } else {
    return await formatHTMLCoreBlock(session, element, sponsored, articleId);
  }
};

const formatHTMLCoreBlock = async (
  session: Session,
  element: HTMLParserElement,
  sponsored?: boolean,
  articleId?: number,
): Promise<ArticleBlock> => {
  const childBlocks: unknown[] = [];
  const extractedElements: unknown[] = [];

  let content = '';
  if (Array.isArray(element?.childNodes) && element?.childNodes.length) {
    content = element.childNodes.map((item: Node) => item.toString()).join('');

    let index = 0;
    for (const childElement of element.childNodes as HTMLParserElement[]) {
      try {
        const block = await formatElementBlock(session, childElement, sponsored, articleId);

        if (block?.tag === 'a') {
          // Kinda a hack --> https://gitlab.benzinga.io/benzinga/fusion/-/work_items/9104?iid_path=true#note_264485
          if (block?.tagAttributes['ref']) {
            block.tagAttributes['ref'] = null;
          }
          let href = block?.tagAttributes['href'];
          href = href?.trim() ?? null;
          block.tagAttributes['target'] = '_blank';
          if (sponsored && typeof href === 'string' && validURL(href) && isExternal(href)) {
            block.tagAttributes['rel'] = 'nofollow noopener noreferrer';
          }
        }

        // Extract elements which cant be inside the P tag to prevent markup break
        if (element.rawTagName === 'p') {
          if (notAllowedInPTags.includes(childElement.rawTagName)) {
            extractedElements.push({
              block,
              index,
            });
            continue;
          }
        }

        childBlocks.push(block);

        // Insert extracted child elements which cant be inside the P tag to prevent markup break
        if (block?.extracted?.length) {
          block?.extracted?.forEach(item => {
            childBlocks.push(item.block);
          });
        }

        index++;
      } catch (error) {
        console.error(`ERROR:: Child Format Error - articleId: ${articleId}`, error);
      }
    }
  } else {
    content = element.rawText;
  }

  const tagAttributes = (await convertAttributesToCamelCase(element?.attributes)) || {};

  // console.log('tagAttributes', tagAttributes);

  if (tagAttributes.className === 'bz-widget' && tagAttributes['data-name']) {
    const postResponse = await session
      .getManager(ContentManager)
      .getWordpressPost(tagAttributes['data-name'] as unknown as number);
    const widget = postResponse.ok;
    // Only carry blocks forward for a successful widget fetch; failed/partial
    // responses must not be enriched later (restores the success guard that
    // previously lived here before enrichment moved to enrichParsedArticleBlocks).
    const widgetBlocks = widget && widget.success !== false && Array.isArray(widget.blocks) ? widget.blocks : undefined;

    return {
      blockName: 'acf/widget-container',
      blocks: widgetBlocks,
      dynamic: false,
      tagAttributes,
      type: 'widget',
    } as unknown as ArticleBlock;
  }

  if (tagAttributes.className === 'bz-read-next-block') {
    const { articleNodes, links, newsSelectionMode, queryAndOptions, title, variant } = getReadNextData({
      childBlocks: (childBlocks as ArticleBlock[]) || [],
      tagAttributes,
    });

    const { options, query } = queryAndOptions;
    const articles =
      newsSelectionMode === 'query'
        ? await simplyQueryInternalNews(session, query, options, { source: 'article-parser:read-next' })
        : await session.getManager(BasicNewsManager).getStoriesWithIds(articleNodes as number[]);

    const articlesWithLinks = articles?.ok?.map(article => {
      const link = links.find(link => link.href.includes(article.url));
      return {
        article,
        href: link?.href ?? article?.url,
        text: link?.text ?? article?.title,
      };
    });

    return {
      attrs: {
        data: {
          links: articlesWithLinks,
          news_selection_mode: newsSelectionMode,
          title,
          variant,
        },
      },
      blockName: 'acf/read-next',
    } as unknown as ArticleBlock;
  }

  if (tagAttributes.className === 'bz-see-more-ranking-block') {
    const { buildSeeMoreRankingShell } = await import('@benzinga/blocks-utils');
    return {
      attrs: {
        data: buildSeeMoreRankingShell(tagAttributes),
        tagAttributes,
      },
      blockName: 'acf/see-more-ranking',
    } as unknown as ArticleBlock;
  }

  return {
    blockName: 'core/html',
    childBlocks: childBlocks ?? [],
    extracted: extractedElements ?? [],
    hasChild: !!(Array.isArray(element?.childNodes) && element?.childNodes.length),
    innerHTML: content ? decode(content) : '',
    nodeType: element?.nodeType ?? 0,
    tag: element?.rawTagName ? element?.rawTagName.toLowerCase() : '',
    tagAttributes,
  } as ArticleBlock;
};

export const convertAttributesToCamelCase = async (attributes: {
  [key: string]: any;
}): Promise<{ [key: string]: any }> => {
  try {
    const attrs = {};

    if (attributes) {
      const { TAG_KEYS_MAP } = await import('../assets/markup/tag-map');
      Object.keys(attributes).forEach(key => {
        attrs[TAG_KEYS_MAP[key] || key] = attributes[key];
      });
    }

    return attrs;
  } catch (error) {
    console.error('Error converting attributes to camel case:', error);
    return {};
  }
};

export const deduplicateElements = (
  elementString: string,
  firstBlock: ArticleBlock,
  secondBlock: ArticleBlock,
  articleBlocks: ArticleBlock[],
  currentIndex: number,
) => {
  if (!elementString) return articleBlocks;
  if (
    (firstBlock?.tag === 'p' || secondBlock?.tag === 'p') &&
    (firstBlock.tag === 'img' || firstBlock?.innerHTML?.slice(0, elementString.length).includes(elementString)) &&
    (secondBlock.tag === 'img' || secondBlock?.innerHTML?.slice(0, elementString.length).includes(elementString)) &&
    secondBlock?.innerHTML?.includes(firstBlock?.innerHTML ?? '')
  ) {
    articleBlocks.splice(currentIndex - 1, 1);
  }

  return articleBlocks;
};

export const parseArticleBodyToBlocks = async (
  session: Session,
  body: string,
  sponsored?: boolean,
  articleId?: number,
) => {
  const elements = body ? await splitHTMLContentRaw(body) : [];

  const htmlNodeElements = elements.filter((item: Node) => {
    return !item.rawText || item.rawText?.trim() !== '' || (!!item.rawText && item.childNodes.length);
  });

  const articleBlocks: ArticleBlock[] = [];
  let htmlNodeElementsIndex = 0;

  const processBlock = async (block: HTMLParserElement, index: number) => {
    const formattedBlock = await formatElementBlock(session, block, sponsored, articleId);

    // Handle remove Twitter script as part of Twitter embed
    if (
      formattedBlock?.tag === 'figure' &&
      formattedBlock.tagAttributes?.className?.includes('twitter') &&
      Array.isArray(formattedBlock.childBlocks)
    ) {
      formattedBlock.childBlocks = formattedBlock.childBlocks.map(child => {
        if (
          typeof child === 'object' &&
          child.tag === 'div' &&
          child.tagAttributes?.className === 'wp-block-embed__wrapper'
        ) {
          if (Array.isArray(child.childBlocks)) {
            child.childBlocks = child.childBlocks.filter(
              block => block && typeof block === 'object' && 'tag' in block && block.tag !== 'script',
            );
          }

          if (typeof child.innerHTML === 'string') {
            child.innerHTML = child.innerHTML.replace(/<script[^>]*>.*?<\/script>/g, '');
          }
        }
        return child;
      });
    }

    articleBlocks.push(formattedBlock);

    if (formattedBlock?.extracted?.length) {
      formattedBlock.extracted.forEach(item => {
        const previousBlock = articleBlocks[index - 1];

        // Hack to solve duplicate table/image issue
        deduplicateElements('<table', previousBlock, formattedBlock, articleBlocks, index);

        // Hack to remove duplicate images wrapped in P tag
        if (
          typeof formattedBlock.innerHTML === 'string' &&
          formattedBlock.innerHTML.includes('<img') &&
          item.block.tag === 'img' &&
          item.block?.tagAttributes?.src &&
          formattedBlock.innerHTML.includes(item.block.tagAttributes.src)
        ) {
          articleBlocks.pop();
        }
        articleBlocks.push(item.block);
      });
    }
  };

  for (const block of htmlNodeElements) {
    htmlNodeElementsIndex++;
    try {
      await processBlock(block as HTMLParserElement, htmlNodeElementsIndex);
    } catch (error) {
      console.error('ERROR:: Error format parent element block', error);
    }
  }

  return articleBlocks;
};
